import React, { useState } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Alert } from '../../ui/Alert';
import SelectMenu from '../../ui/SelectMenu';
import { useResourceCategories } from '../../../hooks/useResourceCategories';

/**
 * ResourceForm — Settings-shape create/edit form for `list_type='resource_types'`
 * records. Extracted from `ResourceTypesPage.js` per PT-22 W3-fp1 F1a so a
 * second consumer (WOCreateSheet's InlineCreate modal) can render the same
 * form and produce Settings-equivalent records rather than name-only stubs.
 *
 * Props:
 *   item        object|null — existing record for edit mode; null for create.
 *   category    string       — pre-selected category (e.g., 'equipment').
 *                              Empty/null = category-chooser flow.
 *   onSave(payload)  fn       — called with { name, description, category };
 *                              may throw to surface a save error inline.
 *   onCancel()       fn       — dismiss the form.
 *
 * Rule 10 (populate FROM + save BACK): the payload shape here is exactly the
 * shape `settingsAPI.addListItem('resource_types', ...)` expects, so inline-
 * created records are first-class in Settings › Resource Types.
 *
 * ── PR-72 Commit 3 (D-07) — category chooser ────────────────────────────
 *
 * When `category` is empty/null (the WO-create-sheet InlineCreate path),
 * the form now renders a DROPDOWN of existing categories, followed by a
 * "+ Custom category…" row that reveals a free-text input.
 *
 * Category source: useResourceCategories() — built-ins ∪ distinct customs
 * on existing resource rows. Do not read from the vocab seed here — see
 * the hook's docblock (dispatch 412 §1) for why the seed is the wrong
 * source. Consumers must never re-derive categories inline.
 *
 * Cascade (dispatch 412 §2):
 *   LOADING   — dropdown shows built-ins immediately (hook contract).
 *   POPULATED — built-ins + customs + custom row.
 *   ERROR     — built-ins shown; a small notice discloses that customs
 *               couldn't be loaded (Family-C shape: a partial list must
 *               not read as complete).
 *   EMPTY     — unreachable (built-ins are unconditional).
 *
 * When the fixed-category path is used (Settings → Resource Types per-tab
 * creation), the branch below is skipped entirely and the form renders
 * exactly as before D-07.
 */

// Module-private sentinel for the "+ Custom category…" dropdown row.
const CUSTOM_SENTINEL = '__custom__';

export default function ResourceForm({ item, category, onSave, onCancel }) {
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');

  const isCustom = !category;

  // D-07 state — only used in the chooser branch. Init to the row's own
  // category if editing an existing resource, or to CUSTOM_SENTINEL if
  // the row's category isn't a known one (defensive; unlikely).
  const [selectedCategory, setSelectedCategory] = useState(item?.category || '');
  const [customCategory, setCustomCategory] = useState(
    // Only prefill when the initial category isn't a first-class option.
    item?.category && !['equipment', 'vehicles', 'tools'].includes(item.category)
      ? item.category
      : ''
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Hook is called unconditionally per Rules of Hooks. Its data is only
  // read in the isCustom branch, but the call must happen every render.
  const { categories, error: catsError } = useResourceCategories();

  const customMode = selectedCategory === CUSTOM_SENTINEL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Resolve category. Fixed-category path unchanged. Chooser path:
    //   - a chosen dropdown value → use it as-is
    //   - custom mode → trim the free-text and require non-empty
    let finalCategory;
    if (!isCustom) {
      finalCategory = category;
    } else if (customMode) {
      finalCategory = customCategory.trim();
      if (!finalCategory) {
        setError('Category is required');
        return;
      }
    } else if (selectedCategory) {
      finalCategory = selectedCategory;
    } else {
      setError('Category is required');
      return;
    }

    try {
      setSaving(true);
      await onSave({ name: name.trim(), description: description.trim(), category: finalCategory });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      {isCustom && (
        <div data-testid="resource-category-chooser">
          <label htmlFor="resource-category" className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <SelectMenu
            id="resource-category"
            width="full"
            value={selectedCategory}
            onChange={(v) => setSelectedCategory(v || '')}
            placeholder="Select a category…"
            options={[
              ...categories.map((c) => ({ value: c.key, label: c.label })),
              { value: CUSTOM_SENTINEL, label: '+ Custom category…' },
            ]}
          />

          {catsError && (
            // Family-C disclosure: don't let a partial list read as complete.
            // Built-ins render regardless; the notice states what's missing.
            <p
              data-testid="resource-category-partial-notice"
              className="mt-1 text-xs text-amber-700"
            >
              Custom categories couldn't be loaded. Built-in categories are still available; try again after refreshing.
            </p>
          )}

          {customMode && (
            <div className="mt-2">
              <label
                htmlFor="resource-custom-category"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                New category name <span className="text-red-500">*</span>
              </label>
              <Input
                id="resource-custom-category"
                data-testid="resource-custom-category-input"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="e.g., Safety Gear"
                required
              />
            </div>
          )}
        </div>
      )}

      <div>
        <label htmlFor="resource-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="resource-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`e.g., ${category === 'equipment' ? 'Spray Rig' : category === 'vehicles' ? 'Box Truck' : 'Drill'}`}
          required
          autoFocus
        />
      </div>

      {/* Iter18 §2.13: description shown next to name in list rows. */}
      <div>
        <label htmlFor="resource-description" className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-xs font-normal text-gray-500">(optional)</span>
        </label>
        <Input
          id="resource-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={category === 'vehicles' ? 'e.g., Red equipment truck' : 'e.g., Backup unit'}
        />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
      </div>
    </form>
  );
}
